/**
 * useFrameScheduler Hook
 * 
 * Manages the FrameScheduler Web Worker lifecycle.
 * Provides unthrottled frame timing for canvas recording,
 * enabling stable 30 FPS even when browser tab is hidden.
 * 
 * Usage:
 *   const { start, stop, isRunning, frameNumber } = useFrameScheduler({
 *     onTick: (frame, timestamp) => { renderFrame(); }
 *   });
 * 
 * @see Base Docs/Canvas_Recording_Implementation_Plan.md - Task 1.4
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { RECORDING_FLAGS } from '../constants/recordingFlags';
import type { FrameSchedulerMessage } from '../worker/frameScheduler.worker';
// @ts-ignore - worker-loader handles this import
import FrameSchedulerWorker from '../worker/frameScheduler.worker';

export interface UseFrameSchedulerOptions {
  fps?: number;
  onTick?: (frameNumber: number, timestamp: number) => void;
  onStarted?: () => void;
  onStopped?: () => void;
  onError?: (error: string) => void;
}

export interface UseFrameSchedulerReturn {
  start: () => void;
  stop: () => void;
  isRunning: boolean;
  frameNumber: number;
  workerEnabled: boolean; // True if Worker is available and flag is enabled
}

// Error recovery constants (Phase 3 - Task 3.3)
const MAX_RESTART_ATTEMPTS = 3;
const BASE_RESTART_DELAY_MS = 100;
const MAX_RESTART_DELAY_MS = 2000;

// MODULE-LEVEL SINGLETON: Shared worker instance across all hook consumers
// This prevents multiple workers from being created (one per DraggableResizableStream)
let sharedWorker: Worker | null = null;
let sharedWorkerStarted = false;
let sharedWorkerCreating = false;

// LEADER ELECTION: Only ONE listener should render the canvas per tick
// Other useVideoTicker instances should only register their video elements
// This prevents double-rendering when multiple DraggableResizableStream components exist
let renderLeader: ((frame: number, timestamp: number) => void) | null = null;
let renderLeaderId: string | null = null;

// MAIN THREAD BLOCKING FIX: Rate-limited rendering
// Problem: Worker sends 30 msg/sec, but hidden tab only processes ~15/sec
// Result: Messages queue up, flood main thread when tab becomes visible
// Solution: Rate limit rendering - only render if enough time has passed
let lastProcessedFrame = 0;
let skippedFrameCount = 0;
let lastSkipReportTime = 0;
let messageQueueDepth = 0;  // Track how many messages we're behind
let lastMessageReceiveTime = 0;
let lastRenderTime = 0;  // Rate limiting: track when we last rendered
const MIN_RENDER_INTERVAL_MS = 30;  // ~33fps max, prevents flooding

export function useFrameScheduler(options: UseFrameSchedulerOptions = {}): UseFrameSchedulerReturn {
  const { fps = 30, onTick, onStarted, onStopped, onError } = options;
  
  const workerRef = useRef<Worker | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [frameNumber, setFrameNumber] = useState(0);
  
  // Error recovery state (Phase 3)
  const restartAttemptsRef = useRef(0);
  const restartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldRestartRef = useRef(false);
  
  // Guard against multiple simultaneous worker creations (per-instance, backup for module guard)
  const isCreatingWorkerRef = useRef(false);
  
  // Store callbacks in refs to avoid recreating worker on callback changes
  const callbacksRef = useRef({ onTick, onStarted, onStopped, onError });
  callbacksRef.current = { onTick, onStarted, onStopped, onError };

  const createWorker = useCallback(() => {
    // If feature flag is disabled, return null - fallback to RAF in main thread
    if (!RECORDING_FLAGS.WORKER_SCHEDULER) {
      return null;
    }
    
    try {
      // Create worker using worker-loader (same pattern as other workers in this codebase)
      const worker = new FrameSchedulerWorker() as Worker;
      
      worker.onmessage = (event: MessageEvent<FrameSchedulerMessage>) => {
        const { type, frameNumber: frame, timestamp, error } = event.data;
        const now = performance.now();
        
        switch (type) {
          case 'tick':
            if (frame !== undefined && timestamp !== undefined) {
              // MAIN THREAD BLOCKING FIX: Track message timing
              const timeSinceLastMessage = now - lastMessageReceiveTime;
              lastMessageReceiveTime = now;
              
              // Calculate how many frames we're behind (messages queued in browser)
              const framesBehind = frame - lastProcessedFrame - 1;
              messageQueueDepth = Math.max(0, framesBehind);
              
              // FRAME SKIPPING: If we're behind, skip intermediate frames
              // This prevents the main thread from being flooded when catching up
              if (framesBehind > 0) {
                skippedFrameCount += framesBehind;
                
                // Report skipped frames every second (throttled)
                if (now - lastSkipReportTime > 1000) {
                  console.warn(`[useFrameScheduler] ⚠️ SKIPPED ${skippedFrameCount} frames (queue depth: ${messageQueueDepth}, ${timeSinceLastMessage.toFixed(1)}ms since last)`);
                  lastSkipReportTime = now;
                  skippedFrameCount = 0;  // Reset counter after reporting
                }
              }
              
              // Always update to latest frame (skip to current, don't process backlog)
              lastProcessedFrame = frame;
              
              // RATE LIMITING: Only render if enough time has passed since last render
              // This prevents main thread flooding when processing queued messages
              const timeSinceLastRender = now - lastRenderTime;
              const shouldRender = timeSinceLastRender >= MIN_RENDER_INTERVAL_MS;
              
              // CRITICAL FIX: Only update React state when we actually render
              // Previously: setFrameNumber was called for EVERY message (150+ setState calls when queue drains!)
              // Now: Only call setState when we render, preventing React from thrashing
              
              // LEADER ELECTION: Only call the render leader (prevents double rendering)
              if (renderLeader && shouldRender) {
                lastRenderTime = now;
                
                // Only update React state when we actually render (not for skipped frames)
                setFrameNumber(frame);
                
                                
                try {
                  renderLeader(frame, timestamp);
                } catch (e) {
                  console.error('[useFrameScheduler] Render leader error:', e);
                }
              }
            }
            break;
          case 'started':
            // Reset rate limiting state for new recording session
            lastProcessedFrame = 0;
            skippedFrameCount = 0;
            lastSkipReportTime = 0;
            messageQueueDepth = 0;
            lastMessageReceiveTime = performance.now();
            lastRenderTime = 0;
            sharedWorkerStarted = true;
            setIsRunning(true);
            callbacksRef.current.onStarted?.();
            break;
          case 'stopped':
            sharedWorkerStarted = false;
            setIsRunning(false);
            callbacksRef.current.onStopped?.();
            break;
          case 'error':
            callbacksRef.current.onError?.(error || 'Unknown worker error');
            break;
        }
      };
      
      worker.onerror = (error) => {
        console.error('[useFrameScheduler] Worker error:', error);
        callbacksRef.current.onError?.(error.message);
        
        // Phase 3 - Error Recovery: Attempt restart with exponential backoff
        if (shouldRestartRef.current && restartAttemptsRef.current < MAX_RESTART_ATTEMPTS) {
          const delay = Math.min(
            BASE_RESTART_DELAY_MS * Math.pow(2, restartAttemptsRef.current),
            MAX_RESTART_DELAY_MS
          );
          restartAttemptsRef.current++;
          console.warn(`[useFrameScheduler] Worker crashed, attempting restart ${restartAttemptsRef.current}/${MAX_RESTART_ATTEMPTS} in ${delay}ms`);
          
          // Terminate crashed worker
          if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = null;
          }
          
          // Schedule restart
          restartTimeoutRef.current = setTimeout(() => {
            if (shouldRestartRef.current) {
              const newWorker = createWorker();
              if (newWorker) {
                workerRef.current = newWorker;
                newWorker.postMessage({ type: 'start', fps });
              }
            }
          }, delay);
        }
      };
      
      return worker;
    } catch (error) {
      console.error('[useFrameScheduler] Failed to create worker:', error);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fps]);

  const start = useCallback(() => {
    // Phase 3: Enable restart recovery and reset attempts
    shouldRestartRef.current = true;
    restartAttemptsRef.current = 0;
    
    // Generate unique ID for this instance
    const instanceId = `ticker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    
    // LEADER ELECTION: First caller with onTick becomes render leader
    // If no current leader and this instance has a tick handler, elect it
    if (!renderLeader && callbacksRef.current.onTick) {
      renderLeader = callbacksRef.current.onTick;
      renderLeaderId = instanceId;
    }
    
    // If shared worker already exists (running or not), reuse it
    // This is critical for avoiding worker restart during component remounts
    if (sharedWorker) {
      workerRef.current = sharedWorker;
      
      if (sharedWorkerStarted) {
        setIsRunning(true);
        callbacksRef.current.onStarted?.();
      } else {
        // Worker exists but not started, send start message
        sharedWorker.postMessage({ type: 'start', fps });
      }
      return;
    }
    
    // Guard against multiple simultaneous worker creations at module level
    if (sharedWorkerCreating) {
      return;
    }
    
    sharedWorkerCreating = true;
    isCreatingWorkerRef.current = true;
    const worker = createWorker();
    isCreatingWorkerRef.current = false;
    sharedWorkerCreating = false;
    
    if (worker) {
      sharedWorker = worker;
      workerRef.current = worker;
      worker.postMessage({ type: 'start', fps });
    } else {
      console.warn('[useFrameScheduler] Worker creation returned null - flag disabled or creation failed');
    }
    // When WORKER_SCHEDULER is disabled or worker creation fails,
    // do NOT call onStarted here - the consumer (useVideoTicker) should
    // detect that workerEnabled=false and use its existing RAF-based timing.
    // This ensures zero regression when the flag is off.
  }, [fps, createWorker]);

  const stop = useCallback(() => {
    // Phase 3: Disable restart recovery and cancel any pending restart
    shouldRestartRef.current = false;
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    
    // If this instance was the render leader, clear it but keep worker running
    // Worker will continue ticking with no listener until a new leader or explicit stop
    if (renderLeader === callbacksRef.current.onTick) {
      renderLeader = null;
      renderLeaderId = null;
    }
    
    // NOTE: We intentionally do NOT stop the worker here
    // This allows fast remount recovery without worker restart overhead
    
    setIsRunning(false);
    setFrameNumber(0);
    callbacksRef.current.onStopped?.();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Phase 3: Disable restart and clear pending timeout
      shouldRestartRef.current = false;
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      
      // If this instance was the render leader, clear leadership but DON'T terminate worker
      // A new leader will register shortly (component remount during layout changes)
      if (renderLeader === callbacksRef.current.onTick) {
        renderLeader = null;
        renderLeaderId = null;
        // NOTE: We intentionally keep the worker running to avoid restart overhead
        // The worker will just tick without a listener until a new leader registers
      }
      
      workerRef.current = null;
    };
  }, []);

  // Check if worker is enabled (flag on and Worker API available)
  const workerEnabled = RECORDING_FLAGS.WORKER_SCHEDULER && typeof Worker !== 'undefined';

  return {
    start,
    stop,
    isRunning,
    frameNumber,
    workerEnabled,
  };
}

export default useFrameScheduler;
